import React, { useEffect, useRef, useState, useCallback } from 'react';
import { chatStyles } from '@zonofi/chats-common';
import { FileParams, theme } from '@zonofi/common';
import { ActivityIndicator } from 'react-native-paper';
import Icon from 'react-native-easy-icon';
import { SHARED_SVGS } from '@zonofi/shared';
import './audio-bubble.scss';
import { useMediaHandler } from '../../helpers/handle-media';
import {
  dispatch,
  MultiToastType,
  updateActionToastData,
} from '@zonofi/seller-rtk-store';

export const AudioBubble: React.FC<FileParams> = (props) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const progressBarRef = useRef<HTMLDivElement | null>(null);

  const hasRetriedRef = useRef(false);
  const permanentFailureRef = useRef(false);
  const isRetryingRef = useRef(false); // ✅ Prevent concurrent retries

  const [didFail, setDidFail] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  const { handleMedia, isLoading } = useMediaHandler({
    ...props,
    maxRetries: 0,
    onPlay: async (url: string) => {
      const audio = audioRef.current;
      if (!audio) return;

      // Prevent play when last failure is unresolved
      if (didFail && permanentFailureRef.current) return;

      audio.pause();
      audio.src = url;

      await new Promise<void>((resolve) => {
        const onCanPlay = () => {
          audio.removeEventListener('canplay', onCanPlay);
          resolve();
        };
        audio.addEventListener('canplay', onCanPlay);
      });

      try {
        await audio.play();
        setIsPlaying(true);
        hasRetriedRef.current = false;
        isRetryingRef.current = false; // ✅ Reset retry flag
        setDidFail(false);
        permanentFailureRef.current = false;
      } catch (err) {
        console.warn('audio.play() failed:', err);
        isRetryingRef.current = false; // ✅ Reset on play error too
      }
    },
  });

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, '0');
    return `${minutes}:${seconds}`;
  };

  const togglePlayPause = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    // If src was cleared after a failure → fetch new link
    if (!audio.src || didFail) {
      // ✅ Reset permanent failure when user manually retries
      if (permanentFailureRef.current) {
        permanentFailureRef.current = false;
        hasRetriedRef.current = false;
      }
      setDidFail(false);
      await handleMedia('open');
      return;
    }

    try {
      if (audio.paused) {
        await audio.play();
        setIsPlaying(true);
      } else {
        audio.pause();
        setIsPlaying(false);
      }
    } catch (err) {
      console.warn('toggle play failed:', err);
    }
  };

  const download = async (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    await handleMedia('download');
  };

  const handleSeek = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    const progressBar = progressBarRef.current;
    if (!audio || !progressBar) return;

    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const width = rect.width;
    const newTime = (clickX / width) * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  // ✅ Use useCallback to stabilize the error handler reference
  const handleAudioError = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio) return;

    // ✅ Prevent infinite errors when src is empty
    if (!audio.src) {
      console.warn('Audio error ignored — empty src.');
      return;
    }

    // ✅ Skip if permanently failed (prevents infinite toast)
    if (permanentFailureRef.current) {
      console.warn('Audio permanently failed — ignoring further errors.');
      return;
    }

    // ✅ Skip if already retrying (prevents concurrent retry attempts)
    if (isRetryingRef.current) {
      console.warn('Skipping duplicate retry — already in progress.');
      return;
    }

    // Helper to clean up audio state
    const clearAudioState = () => {
      audio.pause();
      audio.src = '';
    };

    // First failure → retry once
    if (!hasRetriedRef.current) {
      hasRetriedRef.current = true;
      isRetryingRef.current = true;
      setDidFail(true);

      console.warn('Audio failed to load — retrying once (forced S3).');
      clearAudioState();

      setTimeout(async () => {
        try {
          await handleMedia('open', true);
        } catch (err) {
          console.error('Retry handleMedia failed:', err);
        } finally {
          isRetryingRef.current = false; // ✅ Always reset retrying flag
        }
      }, 300);
      return;
    }

    // ✅ Final failure: Second error after retry → show toast ONCE, mark as permanent
    console.error('Audio failed again — permanent failure.');

    permanentFailureRef.current = true;
    isRetryingRef.current = false;
    setDidFail(true);

    clearAudioState();
    audio.onerror = null; // Detach to stop loop

    dispatch(
      updateActionToastData({
        visibility: true,
        title: 'Audio Playback Error',
        description:
          'Unable to play this audio file. Tap the play button to try again.',
        type: MultiToastType.ERROR,
      })
    );
  }, [handleMedia]);
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return () => {};

    const updateTime = () => setCurrentTime(audio.currentTime);
    const setAudioData = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    // ✅ Attach stable error handler
    audio.onerror = handleAudioError;

    audio.addEventListener('timeupdate', updateTime);
    audio.addEventListener('loadedmetadata', setAudioData);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', updateTime);
      audio.removeEventListener('loadedmetadata', setAudioData);
      audio.removeEventListener('ended', handleEnded);
      audio.onerror = null;
    };
  }, [handleAudioError]); // ✅ Re-attach when handler changes

  return (
    <div
      className="chat-flexRowAlCenterJcSpaceBetn audio-view"
      style={{ borderColor: `${theme.text.secondary}80` }}
      onClick={togglePlayPause}
    >
      <div className="chat-flexRowAlCenter file-container">
        <div className="play-icon">
          <Icon
            name={isPlaying ? 'pause' : 'play'}
            type="feather"
            size={20}
            color={theme.text.primary}
          />
        </div>

        <div className="audio-info">
          <audio ref={audioRef} preload="metadata" />

          <div
            className="progress-bar"
            ref={progressBarRef}
            onClick={handleSeek}
          >
            <div
              className="progress"
              style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
            />
          </div>

          <div
            className="duration-text"
            style={chatStyles.fontRegular10Secondary}
          >
            {formatTime(currentTime)} / {formatTime(duration)}
          </div>
        </div>
      </div>

      {isLoading ? (
        <ActivityIndicator size={20} color={theme.text.secondary} />
      ) : (
        <div onClick={download}>
          <img src={SHARED_SVGS.DownloadBlue} alt="Download Audio" />
        </div>
      )}
    </div>
  );
};
